import React from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { AlertCircle, CheckCircle2, Key, Sparkles, Trash2 } from 'lucide-react'

import { useConfig } from '@/components/hooks/use-config'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

import {
  ANTHROPIC_MODELS,
  GOOGLE_MODELS,
  OPEN_AI_MODELS,
  OPEN_ROUTER_MODELS,
} from '@/lib/llm-models'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_index/settings')({
  component: RouteComponent,
})

const providers: {
  value: 'openai' | 'anthropic' | 'google' | 'openrouter'
  label: string
  description: string
  disabled: boolean
}[] = [
  {
    value: 'openai',
    label: 'OpenAI',
    description: `${OPEN_AI_MODELS.length} models`,
    disabled: false,
  },
  {
    value: 'anthropic',
    label: 'Anthropic',
    description: `${ANTHROPIC_MODELS.length} models`,
    disabled: false,
  },
  {
    value: 'google',
    label: 'Google AI',
    description: `${GOOGLE_MODELS.length} models`,
    disabled: false,
  },
  {
    value: 'openrouter',
    label: 'Open Router',
    description: `${OPEN_ROUTER_MODELS.length} models`,
    disabled: false,
  },
]

function RouteComponent() {
  const navigate = useNavigate()

  const { config, setConfig, isConfigured, handleClear } = useConfig()
  const [currentConfig, setCurrentConfig] =
    React.useState<typeof config>(config)

  const handleSave = () => {
    if (
      !currentConfig.provider ||
      !currentConfig.apiKey ||
      !currentConfig.model
    ) {
      return
    }

    setConfig(currentConfig)
    navigate({ to: '/' })
  }

  React.useEffect(() => {
    setCurrentConfig(config)
  }, [config])

  const models = React.useMemo(() => {
    if (currentConfig.provider === 'openai') return OPEN_AI_MODELS
    if (currentConfig.provider === 'anthropic') return ANTHROPIC_MODELS
    if (currentConfig.provider === 'google') return GOOGLE_MODELS
    if (currentConfig.provider === 'openrouter') return OPEN_ROUTER_MODELS
    return []
  }, [currentConfig.provider])

  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-8 text-center">
        <h1 className="mb-2 text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground">
          Configure your LLM provider to enable AI-powered resume tailoring
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className={cn(
                  'flex h-10 w-10 items-center justify-center rounded-lg',
                  isConfigured ? 'bg-primary/10' : 'bg-muted',
                )}
              >
                {isConfigured ? (
                  <CheckCircle2 className="h-5 w-5 text-primary" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-muted-foreground" />
                )}
              </div>
              <div>
                <CardTitle>LLM Configuration</CardTitle>
                <CardDescription>
                  {isConfigured
                    ? 'Your LLM is configured and ready to use'
                    : 'Set up your LLM provider to get started'}
                </CardDescription>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="provider">Provider</Label>
            <Select
              value={currentConfig.provider || undefined}
              onValueChange={(value) =>
                setCurrentConfig((prev) => ({
                  ...prev,
                  provider: value as
                    | ''
                    | 'openai'
                    | 'anthropic'
                    | 'google'
                    | 'openrouter',
                  apiKey: '',
                  model: '',
                }))
              }
            >
              <SelectTrigger
                id="provider"
                className="w-full"
                data-placeholder={'Select a provider'}
              >
                <SelectValue data-placeholder={'Select a provider'} />
              </SelectTrigger>
              <SelectContent>
                {providers.map((p) => (
                  <SelectItem
                    key={p.value}
                    value={p.value}
                    disabled={p.disabled}
                  >
                    <div className="flex flex-row items-center gap-2 py-1">
                      <span className="font-medium">{p.label}</span>
                      <span className="text-xs text-muted-foreground">
                        {p.description}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="apiKey">API Key</Label>
            <div className="relative">
              <Key className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="apiKey"
                type="password"
                value={currentConfig.apiKey}
                onChange={(e) =>
                  setCurrentConfig((prev) => ({
                    ...prev,
                    apiKey: e.target.value,
                  }))
                }
                placeholder="Enter your API key"
                className="pl-10"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Your API key is stored locally and never sent to our servers
            </p>
          </div>

          {currentConfig.provider && (
            <div className="space-y-2">
              <Label htmlFor="model">Model</Label>
              <Select
                value={currentConfig.model}
                onValueChange={(value) =>
                  setCurrentConfig((prev) => ({
                    ...prev,
                    model: value as string,
                  }))
                }
              >
                <SelectTrigger
                  id="model"
                  className="w-full"
                  data-placeholder="Select a model"
                >
                  <SelectValue data-placeholder="Select a model" />
                </SelectTrigger>
                <SelectContent>
                  {models.map((m, idx) => (
                    <SelectItem
                      key={`${idx}-${currentConfig.provider}-${m.model}`}
                      value={m.model}
                    >
                      {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <Button onClick={handleSave} className="flex-1 gap-2">
              <Sparkles className="h-4 w-4" />
              Save Configuration
            </Button>
            {isConfigured && (
              <Button onClick={handleClear} variant="outline" className="gap-2">
                <Trash2 className="h-4 w-4" />
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
